import { Injectable, OnDestroy } from '@angular/core';
import { CoreCacheModel } from '../models/core-cache.model';

interface CacheLibraryInterface {
    cache: Object;
    ttl: number;
    lastExpiryCheck: number; // timestamp
    expiryCheckInterval: number;
}

@Injectable()
export class CoreCacheService implements OnDestroy {

    private readonly localStorageKey = 'agility-cache';

    private cacheLibrary: {
        [key: string ]: CacheLibraryInterface;
    };

    /**
     * Class constructor
     */
    constructor() {
        this.fetchFromLocalStorage();
    }

    /**
     * Cleanup when done
     */
    ngOnDestroy(): void {
        this.cacheLibrary = {};
    }

    /**
     * Create a new cache library
     *
     * @param cacheId              Name of cache library
     * @param timeToLive           Interval, in seconds, that each item in the cache library should remain
     * @param expiryCheckInterval  Interval, in seconds, between expiration checks of all items in library
     */
    public create(
        cacheId: string,
        timeToLive: number,
        expiryCheckInterval: number
    ): void {
        if (!this.cacheLibrary[cacheId]) {
            this.cacheLibrary[cacheId] = {
                cache: {},
                ttl: timeToLive,
                lastExpiryCheck: +new Date(),
                expiryCheckInterval: expiryCheckInterval,
            };
        }
    }

    /**
     * Destroy an existing cache library
     */
    public destroy(
        cacheId: string
    ): void {
        if (this.cacheLibrary[cacheId]) {
            delete this.cacheLibrary[cacheId];
            this.saveToLocalStorage();
        }
    }

    /**
     * Add something to the cache with an expiry set appropriately in the future
     */
    public add(
        cacheId: string,
        key: string,
        itemToCache: any
    ): void {
        if (this.cacheLibrary[cacheId]) {
            this.expiryCheck(cacheId);
            this.cacheLibrary[cacheId].cache[key] = <CoreCacheModel>{
                data: itemToCache,
                expires: +new Date() + this.cacheLibrary[cacheId].ttl * 1000
            };
            this.saveToLocalStorage();
        }
    }

    /**
     * Get something from the cache
     *
     * Recommend logic is to call isInCheck before calling get so calling point can cleanly handle what to do if
     * sought item is not in cache or cache doesn't exist.
     *
     * @returns any|null Returns item from cache or null if not found or cache library doesn't exist
     */
    public get(
        cacheId: string,
        key: string
    ): any {
        let itemToReturn = null;

        if (this.cacheLibrary[cacheId]) {
            const itemsRemoved = this.expiryCheck(cacheId);
            if (this.cacheLibrary[cacheId].cache[key]) {
                itemToReturn = this.cacheLibrary[cacheId].cache[key].data;
            }
            if (itemsRemoved) {
                this.saveToLocalStorage();
            }
        }

        return itemToReturn;
    }

    /**
     * Check if a specified key corresponds to something in the cache
     *
     * Recommend logic is to call isInCheck before calling get so calling point can cleanly handle what to do if
     * sought item is not in cache or cache doesn't exist.
     *
     * @returns boolean | null  Returns true/false if item found/not found in cache; null if the cache non-existent
     */
    public isInCache(
        cacheId: string,
        key: string
    ): boolean|null {
        let check = false;

        if (this.cacheLibrary[cacheId]) {
            this.expiryCheck(cacheId);
            if (this.cacheLibrary[cacheId].cache[key]) {
                check = true;
            }
        } else {
            return null;
        }

        return check;
    }

    /**
     * Remove an element, if it exists, from a cache library
     */
    public remove(
        cacheId: string,
        key: string
    ): void {
        if (this.isInCache(cacheId, key)) {
            delete this.cacheLibrary[cacheId].cache[key];
            this.saveToLocalStorage();
        }
    }

    /**
     * See which objects have expired and should be targeted to be removed from the cache
     */
    private expiryCheck(
        cacheId: string
    ): boolean {
        let didRemoveItems = false;
        const currentTime = +new Date();

        if (this.cacheLibrary[cacheId]) {
            const currentExpiryInterval = (currentTime - this.cacheLibrary[cacheId].lastExpiryCheck) / 1000;
            if (currentExpiryInterval > this.cacheLibrary[cacheId].expiryCheckInterval) {
                for (const key in this.cacheLibrary[cacheId].cache) {
                    if (this.cacheLibrary[cacheId].cache.hasOwnProperty(key)) {
                        const cacheElement: CoreCacheModel = this.cacheLibrary[cacheId].cache[key];
                        if (cacheElement.expires < currentTime) {
                            delete this.cacheLibrary[cacheId].cache[key];
                            didRemoveItems = true;
                        }
                    }
                }
                this.cacheLibrary[cacheId].lastExpiryCheck = +new Date();
            }
        }

        return didRemoveItems;
    }

    /**
     * Saves all of the current caches to local storage
     */
    private saveToLocalStorage(): void {
        window.localStorage.setItem(this.localStorageKey, JSON.stringify(this.cacheLibrary));
    }

    /**
     * Retrieves the caches from local storage, or initializes the empty cache library if local storage fails
     */
    private fetchFromLocalStorage(): void {
        const storedCache = window.localStorage.getItem(this.localStorageKey);
        this.cacheLibrary = {};

        if (storedCache) {
            try {
                this.cacheLibrary = JSON.parse(storedCache);
            } catch (err) {
                console.error('Failed to parse local storage cache', err);
            }
        }
    }

    /**
     * Dump to browser console log the contents of the cache
     */
    public dumpCache(
        cacheId: string
    ): void {
        if (this.cacheLibrary[cacheId]) {
            console.log();
            console.log('Number of elements: ' + Object.keys(this.cacheLibrary[cacheId].cache).length);
            console.log(`Cache expiry lifetime: ${this.cacheLibrary[cacheId].ttl}`);
            console.log(`Last expiration check: ${this.cacheLibrary[cacheId].lastExpiryCheck}`);
            console.log('Cache contents:');
            console.log(this.cacheLibrary[cacheId].cache);
        } else {
            console.log();
            console.log(`ERROR: unable to find cache know as [${cacheId}]`);
        }
    }
}
